import { Button, useTheme } from '@mui/material';
import React, { useState } from 'react'
import useFocusTrap from '../../../hooks/useFocusTrap';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { can, orgErrorMessage, readSession, requestMoreQuota } from '../../../utils/organisationApi';

const LimitReachedModal = ({
  isLimitReachedModalOpen,
  setIsLimitReachedModalOpen,
  message,
}) => {

  const [isRequestingIncrease, setIsRequestingIncrease] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const theme = useTheme();
  const session = readSession();
  // The limit is shared by the organisation and only its Owner can ask for more (spec 5)
  const canRequest = !session.orgsEnabled || can(session, 'manageQuota');
  const modalRef = useFocusTrap(isLimitReachedModalOpen, () => setIsLimitReachedModalOpen(false));

  const handleRequestLimitIncrease = async () => {
    try {
      setIsRequestingIncrease(true);
      setRequestError(null);
      await requestMoreQuota();
      setRequestSent(true);
      setTimeout(() => {
        setIsLimitReachedModalOpen(false);
        setTimeout(() => {
          setRequestSent(false);
        }, 300);
      }, 3000);
    } catch (error) {
      setRequestError(orgErrorMessage(error, "Could not send the request."));
    } finally {
      setIsRequestingIncrease(false);
    }
  };

  return (
    isLimitReachedModalOpen && (
      <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
        <div className="w-full flex justify-center items-start mt-[70px]">
          <div
            ref={modalRef}
            className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col max-sm:mx-2 overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div
              className="modal-header flex justify-between items-center py-4 px-5 border-b"
              style={{
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.secondary.v4,
              }}
            >
              <h2 className="font-semibold">Candidate Limit Reached</h2>
              <button
                onClick={() => setIsLimitReachedModalOpen(false)}
                aria-label="Close pop-up"
                className="h-6 w-6 flex items-center justify-center rounded-full bg-black text-white"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col px-5 sm:px-14 py-9 gap-2">
              <div className="flex items-center justify-center">
                <div
                  className="rounded-full p-3"
                  style={{ backgroundColor: theme.palette.secondary.v3 }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                      fill={theme.palette.primary.main}
                    />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-medium text-center">
                {session.orgsEnabled ? "Your organisation has reached its resume limit" : "You've reached your candidate limit"}
              </h3>
              <p className="text-center text-accent-gray-dark">
                {message
                  || (session.orgsEnabled && !canRequest
                    ? "Uploads are paused for the whole team. Ask your organisation's Owner to request more."
                    : session.orgsEnabled
                      ? "Uploads are paused for the whole team. Would you like to request a higher limit?"
                      : "Would you like to request an increase to your candidate upload limit?")}
              </p>

              {requestSent ? (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 text-center">
                    Thank you! Your request has been sent. Our team will contact you shortly.
                  </p>
                </div>
              ) : requestError ? (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-center">
                    {requestError}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="w-full py-3 px-5 flex justify-end border-t gap-3" style={{ borderColor: theme.palette.secondary.v4 }}>
              {canRequest ? (
              <Button
                onClick={handleRequestLimitIncrease}
                disabled={isRequestingIncrease || requestSent}
                sx={{
                  backgroundColor: theme.palette.info.main,
                  textTransform: "none",
                  color: theme.palette.text.light,
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.secondary.v3,
                    color: theme.palette.text.dark,
                  },
                }}
              >
                {isRequestingIncrease ? (
                  <span className="flex gap-1 items-center">
                    <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4 mr-2"></div>
                    Sending...
                  </span>
                ) : (
                  <span className="text-white font-medium flex gap-1 items-center">
                    Request Increase
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 12 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.00008 0.164062L5.06008 1.10406L8.78008 4.83073H0.666748V6.16406H8.78008L5.06008 9.89073L6.00008 10.8307L11.3334 5.4974L6.00008 0.164062Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                )}
              </Button>
              ) : (
                <Button
                  onClick={() => setIsLimitReachedModalOpen(false)}
                  sx={{ backgroundColor: theme.palette.info.main, textTransform: "none", color: theme.palette.text.light }}
                >
                  OK
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  )
}

export default LimitReachedModal
